import { createWebHistory, createRouter } from "vue-router";
import routes from "./routes";
import appConfig from "../../app.config";
import store from "@/state/store";
import { routingProcess } from "@/helpers/routeHelper";
import { decodeJwtTokenPayload } from "@/helpers/jwtHelper";
import { shiftAuthTypeToTmp } from "@/helpers/authTypeHelper";

const router = createRouter({
  history: createWebHistory(),
  routes,
  mode: "history",
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

function getAuthType() {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    return decodeJwtTokenPayload(token).AuthType || null;
  } catch {
    return null;
  }
}

function redirectToLogin(authType, next) {
  const effectiveAuthType = authType || localStorage.getItem("lastAuthType");

  if (effectiveAuthType) {
    shiftAuthTypeToTmp(effectiveAuthType);
  }

  if (effectiveAuthType === "OTP") {
    next({ name: "GuestLogin" });
  } else if (effectiveAuthType === "SAML") {
    const backendBaseUrl = process.env.VUE_APP_BACKEND_URL;
    window.location.replace(
      `${backendBaseUrl}/api/Identity/Auth/Login?returnUrl=${backendBaseUrl}/api/Identity/Auth/AssertionConsumerService`
    );
    next(false);
  } else {
    // Hiçbir ipucu yok → Landing Page
    next({ name: "LoginLanding" });
  }
}

// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {

  if (routeTo.meta.authRequired) await routingProcess();

  let to = await store.getters["persistItems/getNavigatedToPage"];
  if (to && to === routeTo.name) {
    let params = await store.getters["persistItems/getNavigationParams"];
    if (params) routeTo.params = params;
  }

  let isTokenThere = !!localStorage.getItem("token");
  let value = routeTo.matched;

  if (value.length == 0) {
    let routes = await store.getters["routeAndMenu/getRoutes"];
    if (routes.length == 0 && isTokenThere) {
      await routingProcess();
      routes = await store.getters["routeAndMenu/getRoutes"];
    }
    value = routes.filter((x) => x.name === routeTo.path.split("/").pop());

    if (value.length == 0) {
      if (!isTokenThere) {
        redirectToLogin(null, next);
        return;
      }
      // Token var ama route bulunamadı → 403
      next({ name: "error-403", query: { mainPage: routeTo.name } });
      return;
    }

    if (value[0].meta.requiresParams) {
      let params = await store.getters["persistItems/getNavigationParams"];
      next({ name: value[0].name, params: params });
    } else {
      next({ name: value[0].name });
    }
    return;
  }

  // Auth gerektirmeyen sayfalar
  if (!routeTo.meta.authRequired) {
    store.dispatch("sessionTimer/stop");
    next();
    return;
  }

  // Auth gerektiren sayfa ama token yok
  if (!isTokenThere) {
    redirectToLogin(getAuthType(), next);
    return;
  }

  // Auth gerektiren sayfa ve token var → session timer başlat
  store.dispatch("sessionTimer/start");

  next();
});

router.afterEach((routeTo) => {
  const layout = routeTo.meta.layout || "vertical";
  store.dispatch("layout/changeLayoutType", { layoutType: layout });
});


router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + " | " + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
